<div class="flex flex-col gap-y-2">

    <!-- Screen reader live region for upload status -->
    <span aria-live="polite" class="sr-only">{{ uploadStatus() }}</span>

    @if (completeVideo()) {
    <!-- ── Preview state ─────────────────────────────────────────────────── -->
    <div class="relative flex justify-center rounded-2xl overflow-hidden border border-white/10 bg-zinc-950"
        [class.aspect-square]="size !== 'sm'"
        [class.h-32]="size === 'sm'"
        [class.w-32]="size === 'sm'">

        <input #fileInput type="file" accept="video/*" class="hidden" (change)="selectFile($event)" />

        <!-- Top gradient so the Change button always reads clearly -->
        <div class="absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-black/60 to-transparent z-10 pointer-events-none rounded-t-2xl"></div>

        <!-- Glassmorphism Change button -->
        <button type="button" (click)="fileInput.click()"
            class="absolute top-2.5 right-2.5 z-20 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white/90 bg-white/10 hover:bg-white/20 active:bg-white/15 backdrop-blur-md rounded-full border border-white/20 transition-all duration-150 shadow-sm">
            <svg class="size-3 shrink-0" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                <path d="M13.488 2.513a1.75 1.75 0 0 0-2.475 0L6.75 6.774a2.75 2.75 0 0 0-.718 1.286l-.451 1.953a.5.5 0 0 0 .606.607l1.953-.452a2.75 2.75 0 0 0 1.286-.717l4.262-4.263a1.75 1.75 0 0 0 0-2.475ZM2.75 9.5a.75.75 0 0 0 0 1.5h.093L4.5 12.75H2.75a.75.75 0 0 0 0 1.5h10.5a.75.75 0 0 0 0-1.5H11.5l1.657-1.75H13.25a.75.75 0 0 0 0-1.5h-10.5Z"/>
            </svg>
            Change
        </button>

        <!--
          object-contain: video fills its natural ratio inside the square container.
          Horizontal video → fills full width, letterboxed top/bottom.
          Vertical video   → fills full height, pillarboxed left/right.
          Height is always bounded by aspect-square on the parent — same as before.
        -->
        <video [src]="completeVideo()" controls
            class="w-full h-full object-contain rounded-2xl"></video>
    </div>

    } @else {
    <!-- ── Empty / drop-zone state ─────────────────────────────────────── -->
    <input #fileInput id="video-upload" type="file" name="video-upload" accept="video/*"
        (change)="selectFile($event)" class="sr-only" />
    <div class="flex justify-center rounded-2xl border-2 border-dashed transition-all duration-200 cursor-pointer"
        role="region"
        aria-label="Video upload drop zone"
        [class.aspect-square]="size !== 'sm'"
        [class.h-32]="size === 'sm'"
        [class.w-32]="size === 'sm'"
        [class.p-6]="size !== 'sm'"
        [class.p-4]="size === 'sm'"
        [class.scale-[1.015]]="isDragging()"
        [class]="isDragging()
            ? 'border-primary bg-primary/10 dark:bg-primary/15 shadow-lg shadow-primary/15'
            : 'border-gray-200/60 dark:border-zinc-700/60 hover:border-primary/40 dark:hover:border-primary/35 hover:bg-primary/5'"
        (click)="fileInput.click()"
        (dragover)="onDragOver($event)" (dragleave)="onDragLeave($event)" (drop)="onDrop($event)">

        <div class="text-center flex flex-col items-center justify-center w-full">

            @if (videoLoading()) {
            <!-- ── Upload in-progress ── -->
            <div class="flex flex-col items-center w-full"
                [class.gap-4]="size !== 'sm'"
                [class.gap-2]="size === 'sm'"
                [class.px-2]="size !== 'sm'">

                <!-- Icon + label row -->
                <div class="flex items-center gap-2">
                    <svg class="shrink-0 text-accent animate-pulse"
                        [class.size-5]="size !== 'sm'"
                        [class.size-4]="size === 'sm'"
                        viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M4.5 4.5a3 3 0 0 0-3 3v9a3 3 0 0 0 3 3h8.25a3 3 0 0 0 3-3v-9a3 3 0 0 0-3-3H4.5ZM19.94 18.75l-2.69-2.69V7.94l2.69-2.69c.944-.945 2.56-.276 2.56 1.06v11.38c0 1.336-1.616 2.005-2.56 1.06Z"
                            clip-rule="evenodd" fill-rule="evenodd"/>
                    </svg>
                    @if (size !== 'sm') {
                    <span class="text-sm font-medium text-accent">Uploading video…</span>
                    }
                </div>

                <!-- Progress bar -->
                <div class="w-full flex flex-col gap-1.5">
                    <div class="w-full rounded-full bg-primary/15 dark:bg-primary/25 overflow-hidden"
                        [class.h-2]="size !== 'sm'"
                        [class.h-1.5]="size === 'sm'"
                        role="progressbar"
                        [attr.aria-valuenow]="uploadService.uploadPercentage()"
                        aria-valuemin="0"
                        aria-valuemax="100"
                        [attr.aria-label]="'Upload progress: ' + (uploadService.uploadPercentage()) + '%'">
                        <div class="h-full rounded-full bg-gradient-to-r from-primary to-brand-studio transition-[width] duration-300 ease-out"
                            [style.width.%]="uploadService.uploadPercentage()">
                        </div>
                    </div>
                    @if (size !== 'sm') {
                    <div class="flex justify-between items-center">
                        <span class="text-xs text-zinc-500 dark:text-zinc-400"></span>
                        <span class="text-xs font-semibold text-accent tabular-nums">
                            {{ uploadService.uploadPercentage() }}%
                        </span>
                        <span class="text-xs text-zinc-500 dark:text-zinc-400"></span>
                    </div>
                    }
                </div>

            </div>

            } @else {
            <!-- ── Idle state ── -->
            <!-- Icon -->
            <div class="rounded-xl bg-primary/10 dark:bg-primary/15 flex items-center justify-center transition-colors duration-150"
                [class.size-14]="size !== 'sm'"
                [class.p-3]="size !== 'sm'"
                [class.size-9]="size === 'sm'"
                [class.p-2]="size === 'sm'"
                [class.mb-4]="size !== 'sm'"
                [class.mb-2]="size === 'sm'">
                <svg viewBox="0 0 24 24" fill="currentColor" data-slot="icon" aria-hidden="true"
                    [class.size-8]="size !== 'sm'"
                    [class.size-5]="size === 'sm'"
                    [class]="isDragging() ? 'text-accent' : 'text-accent/70'">
                    <path d="M4.5 4.5a3 3 0 0 0-3 3v9a3 3 0 0 0 3 3h8.25a3 3 0 0 0 3-3v-9a3 3 0 0 0-3-3H4.5ZM19.94 18.75l-2.69-2.69V7.94l2.69-2.69c.944-.945 2.56-.276 2.56 1.06v11.38c0 1.336-1.616 2.005-2.56 1.06Z"
                        clip-rule="evenodd" fill-rule="evenodd" />
                </svg>
            </div>

            <!-- Label -->
            <div class="flex flex-col items-center"
                [class.gap-0.5]="size !== 'sm'"
                [class]="isDragging() ? 'text-accent/90' : 'text-gray-400 dark:text-zinc-500'">
                <label for="video-upload"
                    class="relative cursor-pointer font-semibold text-accent focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-primary hover:text-accent dark:hover:text-primary/85 transition-colors duration-150"
                    [class.text-sm]="size !== 'sm'"
                    [class.text-xs]="size === 'sm'"
                    (click)="$event.stopPropagation()">
                    <span>Upload a video</span>
                </label>
                @if (size !== 'sm') {
                <p class="text-sm">or drag and drop</p>
                }
            </div>
            }

        </div>
    </div>

    @if (size !== 'sm') {
    <p class="mt-0.5 text-xs text-secondary">MP4, MOV or WebM. Max file size depends on your connection.</p>
    }
    }
</div>
